import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, Snackbar, TextField, Typography,
} from '@mui/material';
import {
  inserirDocumentoProcessoService, updateDocumentoProcessoService,
} from '../../../../services/core/processos/processoDocumentoService';
import { extractResponseErrorMessage, isValidationException } from '../../../../helpers/utils/form.utils';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';

const MENSAGEM_ERRO_GENERICA = 'Não foi possível salvar o documento.';
// Campos que o backend pode devolver em `validations` — mesmo nome de campo
// do FormData nativo real (`SalvarDocumentoProcesso.jsx`), usado para decidir
// se um erro de validação é exibível campo a campo ou cai no Snackbar
// genérico (mesmo critério já documentado no CLAUDE.md do módulo — nunca só
// `if (validations)`/`if (validations?.length)`, que trata `[]` como
// "há erro" e produz um mapeamento vazio sem nenhuma mensagem visível).
const CAMPOS_FORMULARIO = ['documentoProcesso', 'tipo', 'descricao'];

/**
 * Primeiro padrão de upload de arquivo do protótipo (AW2-1094 Etapa 4) — sem
 * `InputFileIcon` equivalente em MUI v5 (só existe versão MUI v4 real), input
 * de arquivo simples construído com `<input type="file" hidden>` + `<label>`,
 * sem `accept` (mesma decisão da tela real, `acceptAll` — AC4 da issue exige
 * que nenhuma validação de tipo/tamanho de arquivo client-side seja
 * introduzida, essa responsabilidade é 100% do backend).
 *
 * Modo edição: arquivo sempre `disabled` (não é possível trocar o arquivo de
 * um documento já salvo, mesmo comportamento real) — mostra só o nome do
 * arquivo já anexado (`documento.nomeArquivo`, já disponível na linha da
 * grid, sem fetch adicional).
 *
 * Submit via `FormData` construído manualmente (`new FormData()` + `.append`,
 * sem `<form>` nativo) — payload replica exatamente
 * `salvarDocumentoProcessoController.makePayload` real: `idProcesso`,
 * `tipo`, `descricao`, `serDocumentoOriginal`/`ocultarDocumentoCliente`
 * (booleanos), `documentoProcesso` (File, só em Novo) OU
 * `idProcessoDocumento` (só em Editar) — nunca os dois juntos.
 *
 * Guarda de sessão/duplo-clique + erro de campo seguem o mesmo padrão de
 * `DialogTitulo.jsx` (mesmo hook `useSessaoDialogMutacao`, mesma whitelist
 * `CAMPOS_FORMULARIO` + fallback Snackbar genérico).
 */
function makeFormFieldsDocumento(documento) {
  return {
    idProcessoDocumento: documento?.id ?? null,
    arquivo: null,
    nomeArquivoAtual: documento?.nomeArquivo ?? '',
    tipo: documento?.tipo ?? '',
    descricao: documento?.descricao ?? '',
    serDocumentoOriginal: documento?.originalArquivada ?? false,
    ocultarDocumentoCliente: documento ? documento.oculto : true,
  };
}

function makePayloadDocumento(idProcesso, formFields) {
  const formData = new FormData();
  formData.append('idProcesso', idProcesso);
  formData.append('tipo', formFields.tipo);
  formData.append('descricao', formFields.descricao || '');
  formData.append('serDocumentoOriginal', formFields.serDocumentoOriginal);
  formData.append('ocultarDocumentoCliente', formFields.ocultarDocumentoCliente);

  if (formFields.idProcessoDocumento) {
    formData.append('idProcessoDocumento', formFields.idProcessoDocumento);
  } else {
    formData.append('documentoProcesso', formFields.arquivo);
  }

  return formData;
}

export default function DialogSalvarDocumentoProcesso({
  open, documento, isEditar, idProcesso, onClose, onSucesso,
}) {
  const [formFields, setFormFields] = useState(() => makeFormFieldsDocumento(documento));
  const [errosPorCampo, setErrosPorCampo] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    setFormFields(makeFormFieldsDocumento(documento));
    setErrosPorCampo({});
    setErro('');
    setEnviando(false);
  });

  const onChangeCampo = nome => (event) => {
    const { value } = event.target;
    setFormFields(fields => ({ ...fields, [nome]: value }));
    setErrosPorCampo(erros => ({ ...erros, [nome]: undefined }));
  };

  const onChangeCheckbox = nome => (event) => {
    const { checked } = event.target;
    setFormFields(fields => ({ ...fields, [nome]: checked }));
  };

  const onChangeArquivo = (event) => {
    const arquivo = event.target.files?.[0] ?? null;
    setFormFields(fields => ({ ...fields, arquivo }));
    setErrosPorCampo(erros => ({ ...erros, documentoProcesso: undefined }));
  };

  const onSubmit = async () => {
    if (enviandoRef.current) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;
    setEnviando(true);
    setErrosPorCampo({});
    setErro('');

    try {
      const payload = makePayloadDocumento(idProcesso, formFields);
      if (formFields.idProcessoDocumento) {
        await updateDocumentoProcessoService(payload);
      } else {
        await inserirDocumentoProcessoService(payload);
      }

      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      onSucesso();
      onClose();
    } catch (exception) {
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      if (isValidationException(exception)) {
        const validations = exception.response.data.validations || [];
        const errosMapeados = validations.reduce((acc, validation) => (
          CAMPOS_FORMULARIO.includes(validation.field)
            ? { ...acc, [validation.field]: validation.message }
            : acc
        ), {});

        if (Object.keys(errosMapeados).length > 0) {
          setErrosPorCampo(errosMapeados);
        } else {
          setErro(MENSAGEM_ERRO_GENERICA);
        }
      } else {
        setErro(extractResponseErrorMessage(exception) || MENSAGEM_ERRO_GENERICA);
      }
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  };

  const isDisabledConfirmar = !formFields.tipo.trim() || (!isEditar && !formFields.arquivo);
  const nomeArquivoExibido = isEditar ? formFields.nomeArquivoAtual : (formFields.arquivo?.name || '');

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditar ? 'Editar Documento' : 'Novo Documento'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <label htmlFor="input-arquivo-documento-processo">
                  <input
                    id="input-arquivo-documento-processo"
                    type="file"
                    hidden
                    disabled={isEditar}
                    onChange={onChangeArquivo}
                  />
                  <Button component="span" variant="outlined" size="small" disabled={isEditar}>
                    Selecionar Arquivo
                  </Button>
                </label>
                <Typography
                  variant="body2"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {nomeArquivoExibido || 'Nenhum arquivo selecionado'}
                </Typography>
              </Box>
              {errosPorCampo.documentoProcesso && (
                <Typography variant="caption" color="error">{errosPorCampo.documentoProcesso}</Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tipo*"
                value={formFields.tipo}
                onChange={onChangeCampo('tipo')}
                error={Boolean(errosPorCampo.tipo)}
                helperText={errosPorCampo.tipo}
                inputProps={{ maxLength: 50 }}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={formFields.descricao}
                onChange={onChangeCampo('descricao')}
                error={Boolean(errosPorCampo.descricao)}
                helperText={errosPorCampo.descricao}
                inputProps={{ maxLength: 200 }}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={formFields.serDocumentoOriginal}
                    onChange={onChangeCheckbox('serDocumentoOriginal')}
                  />
                )}
                label="Documento original está arquivado na ABE"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={formFields.ocultarDocumentoCliente}
                    onChange={onChangeCheckbox('ocultarDocumentoCliente')}
                  />
                )}
                label="Ocultar o documento do Cliente"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={onSubmit} variant="contained" disabled={enviando || isDisabledConfirmar}>
            {isEditar ? 'Confirmar' : 'Anexar'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogSalvarDocumentoProcesso.propTypes = {
  open: PropTypes.bool,
  documento: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tipo: PropTypes.string,
    descricao: PropTypes.string,
    nomeArquivo: PropTypes.string,
    originalArquivada: PropTypes.bool,
    oculto: PropTypes.bool,
  }),
  isEditar: PropTypes.bool,
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onSucesso: PropTypes.func.isRequired,
};

DialogSalvarDocumentoProcesso.defaultProps = {
  open: false,
  documento: null,
  isEditar: false,
  idProcesso: null,
};
